import { useEffect, useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  format,
  isSameMonth,
  isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUndoableDelete } from '@/hooks/useUndoableDelete';
import api from '@/lib/api';
import { DndCalendarWrapper } from '@/components/calendar/DndCalendarWrapper';
import { DroppableDay } from '@/components/calendar/DroppableDay';
import { CalendarAgenda } from '@/components/calendar/CalendarAgenda';
import { getUserColor, formatTime12Hour } from '@/utils/calendarHelpers';
import { DAY_LABELS } from '@/utils/constants';
import { DayCell } from './DayCell';
import { DayModal } from './DayModal';
import { RecurringClaimSection } from './RecurringClaimSection';
import { UnfilledSlotsAlert } from './UnfilledSlotsAlert';
import { EarningsSummary } from '@/components/dashboard/EarningsSummary';
import { PaymentAlert } from '@/components/dashboard/PaymentAlert';
import type { DayData } from './types';

export function CalendarTab() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const { deleteSlotWithUndo } = useUndoableDelete();

  const isParentOrAdmin = user?.role === 'parent';
  const isCaretaker = user?.role === 'caretaker';
  const isFamily = user?.role === 'family';
  const isProfessional = user?.role === 'professional';

  const monthName = format(currentDate, 'MMMM yyyy');
  const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate visible date range
  const visibleDateRange = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return {
      startDate: format(calendarStart, 'yyyy-MM-dd'),
      endDate: format(calendarEnd, 'yyyy-MM-dd'),
    };
  }, [currentDate]);

  // Fetch availability windows (includes auto_assign info per window)
  const { data: availabilityData = [] } = useQuery({
    queryKey: ['availability', visibleDateRange.startDate, visibleDateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: visibleDateRange.startDate,
        end_date: visibleDateRange.endDate,
      });
      const res = await api.get(`/availability/windows?${params}`);
      const data = res.data;
      const flattened: any[] = [];
      (data.availability || []).forEach((day: any) => {
        (day.windows || []).forEach((w: any) => {
          flattened.push({
            date: day.date,
            start_time: w.start,
            end_time: w.end,
            auto_assign_user_id: w.auto_assign_user_id || null,
            auto_assign_user: w.auto_assign_user || null,
          });
        });
      });
      return flattened;
    },
    staleTime: 1000 * 5,
    refetchInterval: 5000,
  });

  // Fetch time slots
  const { data: timeSlotsData = [] } = useQuery({
    queryKey: ['timeslots', visibleDateRange.startDate, visibleDateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: visibleDateRange.startDate,
        end_date: visibleDateRange.endDate,
        include_caretaker: 'true',
      });
      const res = await api.get(`/timeslots?${params}`);
      return res.data.slots || [];
    },
    staleTime: 1000 * 5,
    refetchInterval: 5000,
  });

  // Generate calendar days
  const calendarDays = useMemo((): DayData[] => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    // Cutoff: don't show history from before this date
    const historyCutoff = new Date('2026-02-16T00:00:00');

    return days.map((date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      const inCurrentMonth = isSameMonth(date, currentDate);
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      const isBeforeCutoff = dayDate < historyCutoff;

      // Before cutoff: no windows or slots
      const allDayWindows = inCurrentMonth && !isBeforeCutoff
        ? (availabilityData || []).filter((w: any) => w.date === dateString)
        : [];
      const daySlots = isBeforeCutoff
        ? []
        : (timeSlotsData || []).filter((s: any) => s.date === dateString);

      // Split windows: auto-assigned (become virtual slots) vs regular (stay green)
      const regularWindows: any[] = [];
      const virtualSlots: any[] = [];

      for (const w of allDayWindows) {
        if (w.auto_assign_user_id && w.auto_assign_user) {
          // Check if a real slot already covers this window
          const hasRealSlot = daySlots.some((s: any) =>
            s.start_time === w.start_time && s.end_time === w.end_time
          );
          if (!hasRealSlot) {
            virtualSlots.push({
              id: `default-${dateString}-${w.start_time}-${w.end_time}`,
              caretaker_id: w.auto_assign_user_id,
              date: dateString,
              start_time: w.start_time,
              end_time: w.end_time,
              status: 'claimed',
              is_default: true,
              caretaker: w.auto_assign_user,
            });
          }
          // Don't add to regularWindows — it's covered by the virtual slot or real slot
        } else {
          regularWindows.push(w);
        }
      }

      return {
        date,
        dateString,
        isCurrentMonth: inCurrentMonth,
        isToday: isToday(date),
        availabilityWindows: regularWindows,
        claimedSlots: [...daySlots, ...virtualSlots],
      };
    });
  }, [currentDate, availabilityData, timeSlotsData]);

  // Extract unique caretakers from time slots + auto-assign windows for the legend
  const caretakersInView = useMemo(() => {
    const caretakerMap = new Map<string, { id: string; first_name: string; color?: string; emoji?: string; role?: string }>();
    (timeSlotsData || []).forEach((slot: any) => {
      if (slot.caretaker && slot.status !== 'cancelled' && !caretakerMap.has(slot.caretaker_id)) {
        caretakerMap.set(slot.caretaker_id, {
          id: slot.caretaker_id,
          first_name: slot.caretaker.first_name,
          color: slot.caretaker.color,
          emoji: slot.caretaker.emoji,
          role: slot.caretaker.role,
        });
      }
    });
    // Also include caretakers from auto-assign availability windows
    (availabilityData || []).forEach((w: any) => {
      if (w.auto_assign_user_id && w.auto_assign_user && !caretakerMap.has(w.auto_assign_user_id)) {
        caretakerMap.set(w.auto_assign_user_id, {
          id: w.auto_assign_user_id,
          first_name: w.auto_assign_user.first_name,
          color: w.auto_assign_user.color,
          emoji: w.auto_assign_user.emoji,
          role: w.auto_assign_user.role,
        });
      }
    });
    return Array.from(caretakerMap.values());
  }, [timeSlotsData, availabilityData]);

  // Keep selectedDay in sync with latest query data so the modal updates instantly
  useEffect(() => {
    if (selectedDay && showModal) {
      const updatedDay = calendarDays.find((d) => d.dateString === selectedDay.dateString);
      if (updatedDay && updatedDay !== selectedDay) {
        setSelectedDay(updatedDay);
      }
    }
  }, [calendarDays, showModal, selectedDay]);

  const handlePrevMonth = () => setCurrentDate((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentDate((prev) => addMonths(prev, 1));
  const handleToday = () => setCurrentDate(startOfMonth(new Date()));

  const handleDayClick = (day: DayData) => {
    if (isProfessional) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayDate = new Date(day.date);
    dayDate.setHours(0, 0, 0, 0);
    if (dayDate < today) return;
    setSelectedDay(day);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDay(null);
  };

  // Handle dropping a slot onto a different day (parent only)
  const handleDropSlot = async (slotData: any, targetDate: string) => {
    try {
      if (slotData.type === 'availability_window') {
        await api.post('/availability/overrides', {
          date: targetDate,
          override_type: 'add_windows',
          windows: [{ start: slotData.start_time, end: slotData.end_time }],
        });
        queryClient.invalidateQueries({ queryKey: ['availability'] });
      } else {
        // Check caretaker day preferences before copying slot
        const targetDayOfWeek = format(new Date(targetDate + 'T00:00:00'), 'EEEE').toLowerCase();
        const caretaker = slotData.caretaker;
        if (caretaker) {
          const isUnavailable = caretaker.unavailable_days?.includes(targetDayOfWeek);
          const isPreferredOff = caretaker.preferred_days_off?.includes(targetDayOfWeek);
          if (isUnavailable) {
            const confirmed = window.confirm(
              `${caretaker.first_name} has marked ${DAY_LABELS[targetDayOfWeek] || targetDayOfWeek} as a day they cannot work. Assign anyway?`
            );
            if (!confirmed) return;
          } else if (isPreferredOff) {
            const confirmed = window.confirm(
              `${caretaker.first_name} prefers ${DAY_LABELS[targetDayOfWeek] || targetDayOfWeek} off. Assign anyway?`
            );
            if (!confirmed) return;
          }
        }

        await api.post('/timeslots/assign', {
          caretaker_id: slotData.caretaker_id,
          date: targetDate,
          start_time: slotData.start_time,
          end_time: slotData.end_time,
        });
        queryClient.invalidateQueries({ queryKey: ['timeslots'] });
        queryClient.invalidateQueries({ queryKey: ['availability'] });
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Remove a specific window from a date (creates an override)
  const handleRemoveWindow = async (
    dateString: string,
    windowToRemove: { start: string; end: string },
    allWindows: { start_time: string; end_time: string }[]
  ) => {
    try {
      const remainingWindows = allWindows.filter(
        (w) => w.start_time !== windowToRemove.start || w.end_time !== windowToRemove.end
      );

      if (remainingWindows.length === 0) {
        await api.delete(`/availability/overrides/by-date?date=${dateString}`);
        await api.post('/availability/overrides', {
          date: dateString,
          override_type: 'remove_all',
        });
      } else {
        const windows = remainingWindows.map(w => ({ start: w.start_time, end: w.end_time }));
        await api.post('/availability/overrides', {
          date: dateString,
          override_type: 'replace_windows',
          windows,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Delete a claimed slot directly from the calendar cell — with undo
  const handleDeleteSlotFromCell = (slotId: string) => {
    deleteSlotWithUndo(slotId, 'Slot removed');
  };

  return (
    <div data-testid="calendar-tab" className="space-y-6">
      {/* Alerts */}
      {isParentOrAdmin && <PaymentAlert />}
      {(isCaretaker || isFamily) && <UnfilledSlotsAlert calendarDays={calendarDays} />}

      {/* Header */}
      <div className="bg-card rounded-xl border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-white">Calendar</h1>
            <p className="text-gray-400 text-sm mt-1">
              {isParentOrAdmin
                ? 'Manage availability and view scheduled care'
                : isCaretaker || isFamily
                ? 'View availability and claim time slots'
                : "View Kai's care schedule"}
            </p>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between mb-4">
          <h2 data-testid="month-name" className="text-lg font-medium text-white">{monthName}</h2>
          <div className="flex items-center gap-2">
            <button
              data-testid="clickable-calendar-today"
              onClick={handleToday}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <Calendar size={14} />
              Today
            </button>
            <button
              data-testid="clickable-calendar-prev-month"
              onClick={handlePrevMonth}
              className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="clickable-calendar-next-month"
              onClick={handleNextMonth}
              className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Calendar Grid (desktop) / Agenda (mobile) */}
        {isMobile ? (
          <CalendarAgenda
            days={calendarDays}
            onDayClick={handleDayClick}
            currentUserId={user?.id}
            isClickable={!isProfessional}
            isParentOrAdmin={!!isParentOrAdmin}
            onUnclaimSlot={(isParentOrAdmin || isCaretaker || isFamily) ? handleDeleteSlotFromCell : undefined}
            getUserColor={getUserColor}
            formatTime12Hour={formatTime12Hour}
          />
        ) : (
          <DndCalendarWrapper
            onDropSlot={isParentOrAdmin ? handleDropSlot : () => {}}
            formatTime12Hour={formatTime12Hour}
            getUserColor={getUserColor}
          >
            <div data-testid="month-calendar" className="border border-slate-700 rounded-lg overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-slate-800">
                {dayHeaders.map((day) => (
                  <div key={day} className="py-1.5 sm:py-2 text-center text-[10px] sm:text-xs font-medium text-gray-400 border-b border-slate-700">
                    {day.slice(0, 3)}
                  </div>
                ))}
              </div>

              {/* Day Cells */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day) => (
                  <DroppableDay
                    key={`drop-${day.dateString}`}
                    id={`drop-${day.dateString}`}
                    dateString={day.dateString}
                    disabled={!isParentOrAdmin}
                  >
                    <DayCell
                      day={day}
                      onClick={() => handleDayClick(day)}
                      currentUserId={user?.id}
                      isClickable={!isProfessional}
                      isParentOrAdmin={isParentOrAdmin}
                      onRemoveWindow={handleRemoveWindow}
                      onDeleteSlot={(isParentOrAdmin || isCaretaker || isFamily) ? handleDeleteSlotFromCell : undefined}
                    />
                  </DroppableDay>
                ))}
              </div>
            </div>
          </DndCalendarWrapper>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-gray-400">Available</span>
          </div>
          {caretakersInView.map((ct) => (
              <div key={ct.id} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getUserColor(ct) }}
                />
                <span className="text-gray-400 flex items-center gap-1">
                  {ct.emoji && <span>{ct.emoji}</span>}
                  {ct.first_name}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Recurring Claim - caretakers only */}
      {isCaretaker && (
        <RecurringClaimSection
          calendarDays={calendarDays}
          currentUser={user!}
          queryClient={queryClient}
        />
      )}

      {/* Summary - shows past completed slots for caretakers and family role */}
      {(isCaretaker || isFamily) && <EarningsSummary userId={user?.id || ''} userRole={user?.role || ''} />}

      {/* Modal */}
      {showModal && selectedDay && (
        <DayModal
          day={selectedDay}
          onClose={closeModal}
          currentUser={user!}
          isParentOrAdmin={!!isParentOrAdmin}
          isCaretaker={!!isCaretaker}
          isFamily={!!isFamily}
          queryClient={queryClient}
        />
      )}
    </div>
  );
}
